import type { ReactNode } from "react";
import {
  Dialog as AriaDialog,
  Button as AriaButton,
  Heading,
  Modal,
  ModalOverlay,
  type ModalOverlayProps,
} from "react-aria-components";
import { useTranslation } from "react-i18next";
import { Icon } from "@core/assets";
import { cn } from "@core/utils";

interface DialogProps extends Omit<
  ModalOverlayProps,
  "className" | "children"
> {
  /**
   * Dialog title — rendered as the labelling `<h2>`, never omitted. A plain string for every
   * caller so far; accepts a richer node too (e.g. a title plus an inline count badge, Figma
   * 346:9106) since `<Heading>` renders either identically — existing string callers are
   * untouched.
   */
  title: ReactNode;
  /** Supporting copy under the title. */
  description?: string;
  children?: ReactNode;
  /** Footer actions, right-aligned. */
  actions?: ReactNode;
  /** Inline max width; dialogs stay full-bleed-minus-gutter at 320px regardless. */
  className?: string;
  /** Draws the × in the header corner (the workflow frames all have it). Off by default so
   * existing dialogs do not change. */
  hasCloseButton?: boolean;
}

/**
 * Modal dialog primitive on React Aria's `Modal` — focus trap, scroll lock, Escape/overlay
 * dismiss, and `aria-labelledby` wiring come from React Aria, so features never hand-roll
 * modal semantics.
 *
 * Mobile-first: full width minus a 16px gutter at 320px, capped by `className` from `sm:` up.
 *
 * **THE TITLE AND THE ACTIONS ARE PINNED; ONLY THE BODY SCROLLS.** This is the whole reason the
 * three regions are separate elements rather than one padded column. A dialog whose content grows —
 * a form that reveals conditional fields, a long list — used to grow with it until the footer was
 * below the fold, which on a form means the Cancel and Save buttons become unreachable and the only
 * way out is Escape. Capping the modal at the viewport and scrolling the middle keeps the way out
 * on screen at every height.
 *
 * Padding lives on the three regions rather than on the dialog so the scrolling edge sits between
 * them: content has to disappear UNDER the header and footer, not under a gap.
 */
export function Dialog({
  title,
  description,
  children,
  actions,
  className,
  hasCloseButton = false,
  ...props
}: DialogProps) {
  const { t } = useTranslation("common");

  return (
    <ModalOverlay
      isDismissable
      className="fixed inset-0 z-50 flex items-center justify-center bg-overlay p-md backdrop-blur-[2px] entering:animate-in entering:fade-in exiting:animate-out exiting:fade-out"
      {...props}
    >
      {/* `max-h-full` measures against the overlay, which is inset-0 with `p-md` — so the modal can
          never exceed the viewport minus that gutter. `flex flex-col` + `min-h-0` on the middle
          region is what makes the body the part that gives way. */}
      <Modal
        className={cn(
          "flex max-h-full w-full max-w-100 flex-col rounded-lg bg-surface-card shadow-lg outline-none",
          "entering:animate-in entering:fade-in entering:zoom-in-95 exiting:animate-out exiting:fade-out exiting:zoom-out-95",
          className
        )}
      >
        {/* `flex-1 min-h-0` rather than just `min-h-0`: as the only child of a capped column, an
            auto-basis item sizes to its content and would spill straight back out of the cap. */}
        <AriaDialog className="flex min-h-0 flex-1 flex-col outline-none">
          {({ close }) => (
            <>
              <div className="relative flex shrink-0 flex-col gap-xxs px-lg pt-lg">
                {hasCloseButton ? (
                  <AriaButton
                    onPress={close}
                    aria-label={t("actions.dismiss")}
                    className={cn(
                      "absolute top-md right-md flex size-8 items-center justify-center rounded-md",
                      "text-(--text-secondary) outline-none transition-colors",
                      "hover:bg-ink-50 pressed:bg-ink-100",
                      "focus-visible:ring-2 focus-visible:ring-focus-ring"
                    )}
                  >
                    <Icon name="close" size={20} />
                  </AriaButton>
                ) : null}
                <Heading
                  slot="title"
                  className={cn(
                    "text-lg-semibold text-(--text-primary)",
                    hasCloseButton && "pe-lg"
                  )}
                >
                  {title}
                </Heading>
                {description ? (
                  <p className="text-sm-regular text-(--text-secondary)">
                    {description}
                  </p>
                ) : null}
              </div>

              {children ? (
                <div
                  className={cn(
                    // `min-h-0` is load-bearing: without it a flex item refuses to shrink below its
                    // content, so the modal would still grow past the viewport and nothing would scroll.
                    "min-h-0 flex-1 overflow-y-auto px-lg pt-md",
                    // The body owns the bottom padding when there is no footer to own it.
                    actions ? "pb-md" : "pb-lg"
                  )}
                >
                  {children}
                </div>
              ) : null}

              {actions ? (
                // Stacked and full-width at 320px, right-aligned from sm: up. Two buttons with real
                // labels ("Schedule opening" / "Open now") do not fit one row inside a dialog on a
                // narrow phone, and half-width wrapped buttons are a worse target than full-width
                // ones. `*:` styles the action buttons without each dialog repeating the classes.
                <div
                  className={cn(
                    "flex shrink-0 flex-col gap-xs px-lg pb-lg *:w-full",
                    children ? "pt-xs" : "pt-md",
                    "sm:flex-row sm:items-center sm:justify-end sm:*:w-auto"
                  )}
                >
                  {actions}
                </div>
              ) : null}
            </>
          )}
        </AriaDialog>
      </Modal>
    </ModalOverlay>
  );
}

export type { DialogProps };
